/*
 * Enhances a Dawn slider-component with a thin scroll-progress bar (LAMA style)
 * and an optional infinite autoplay loop. The progress bar's width reflects how
 * much of the track is visible and it slides as the user scrolls.
 * Activated on any <slider-component data-carousel-autoplay="true|false">.
 */
class FeaturedCarousel {
  constructor(root) {
    this.root = root;
    this.slider = root.querySelector('[id^="Slider-"]');
    this.progressBar = root.querySelector('.slider-progress__bar');
    if (!this.slider) return;

    this.speed = (parseFloat(root.dataset.carouselSpeed) || 4) * 1000;
    this.autoplay = root.dataset.carouselAutoplay === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;
    this.direction = 1;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchMoved = false;
    this.touchStartedOnCarousel = false;
    this.isPointerDragging = false;
    this.pointerDragMoved = false;
    this.pointerDragStartX = 0;
    this.pointerDragStartScrollLeft = 0;
    this.pointerDragStartIndex = 0;
    this.pointerDragDeltaX = 0;
    this.scrollAnimationFrame = null;

    this.updateProgress();
    this.slider.addEventListener('scroll', this.throttle(this.updateProgress.bind(this), 50), { passive: true });
    this.slider.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.slider.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.slider.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.slider.addEventListener('pointercancel', this.onPointerCancel.bind(this));
    this.slider.addEventListener('click', this.onPointerClick.bind(this), true);
    window.addEventListener('resize', this.throttle(this.updateProgress.bind(this), 200));
    window.addEventListener('load', this.updateProgress.bind(this));
    if (this.autoplay && !this.reducedMotion) {
      this.start();
      ['mouseenter', 'focusin'].forEach((evt) => this.root.addEventListener(evt, this.stop.bind(this)));
      ['mouseleave', 'focusout'].forEach((evt) => this.root.addEventListener(evt, this.start.bind(this)));
      this.slider.addEventListener('touchstart', this.stop.bind(this), { passive: true });
      this.slider.addEventListener('touchend', this.restart.bind(this), { passive: true });
    }
  }

  pageWidth() {
    return this.slider.clientWidth || 1;
  }

  maxScroll() {
    return Math.max(1, this.slider.scrollWidth - this.slider.clientWidth);
  }

  pageCount() {
    return Math.max(1, Math.ceil((this.slider.scrollWidth - 1) / this.pageWidth()));
  }

  currentPage() {
    return Math.min(this.pageCount() - 1, Math.round(this.slider.scrollLeft / this.pageWidth()));
  }

  updateProgress() {
    if (!this.progressBar) return;

    const track = this.progressBar.parentElement;
    const hasOverflow = this.slider.scrollWidth > this.slider.clientWidth + 2;

    if (track) track.style.visibility = hasOverflow ? 'visible' : 'hidden';
    if (!hasOverflow) return;

    // Thumb width = fraction of the track that is currently visible.
    const widthPct = Math.min(100, Math.max(14, (this.slider.clientWidth / this.slider.scrollWidth) * 100));
    // Position = scroll progress across the remaining track.
    const progress = this.slider.scrollLeft / this.maxScroll();

    this.progressBar.style.width = `${widthPct}%`;
    this.progressBar.style.left = `${progress * (100 - widthPct)}%`;
  }

  goTo(index) {
    this.smoothScrollTo(index * this.pageWidth());
  }

  smoothScrollTo(left) {
    const max = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
    const target = Math.max(0, Math.min(max, left));
    const start = this.slider.scrollLeft;
    const distance = target - start;

    if (this.scrollAnimationFrame) cancelAnimationFrame(this.scrollAnimationFrame);
    if (Math.abs(distance) < 1 || this.reducedMotion) {
      this.slider.scrollLeft = target;
      return;
    }

    // A small requestAnimationFrame tween is consistent on Safari versions
    // where scrollTo({ behavior: 'smooth' }) and scroll snap can fight.
    const duration = Math.min(520, Math.max(280, Math.abs(distance) * 0.45));
    const startedAt = performance.now();
    this.slider.classList.add('is-programmatic-scrolling');

    const animate = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.slider.scrollLeft = start + distance * eased;

      if (progress < 1) {
        this.scrollAnimationFrame = requestAnimationFrame(animate);
      } else {
        this.scrollAnimationFrame = null;
        this.slider.scrollLeft = target;
        this.slider.classList.remove('is-programmatic-scrolling');
      }
    };

    this.scrollAnimationFrame = requestAnimationFrame(animate);
  }

  slides() {
    return Array.from(this.slider.querySelectorAll('.slider__slide')).filter((slide) => slide.clientWidth > 0);
  }

  currentSlideIndex() {
    const slides = this.slides();
    const center = this.slider.scrollLeft + this.slider.clientWidth / 2;
    let active = 0;
    let min = Infinity;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < min) {
        min = distance;
        active = index;
      }
    });

    return active;
  }

  goToSlide(index) {
    const slides = this.slides();
    if (!slides.length) return;

    const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
    const centeredOffset = target.offsetLeft - (this.slider.clientWidth - target.clientWidth) / 2;
    this.smoothScrollTo(centeredOffset);
  }

  onPointerDown(event) {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    if (
      event.target.closest(
        'a, .card__media-bars, .card__actions, button, input, select, textarea'
      )
    )
      return;

    this.isPointerDragging = true;
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
      this.slider.classList.remove('is-programmatic-scrolling');
    }
    this.pointerDragMoved = false;
    this.pointerDragStartX = event.clientX;
    this.pointerDragStartScrollLeft = this.slider.scrollLeft;
    this.pointerDragStartIndex = this.currentSlideIndex();
    this.pointerDragDeltaX = 0;
    this.slider.classList.add('is-dragging');

    if (this.slider.setPointerCapture) {
      this.slider.setPointerCapture(event.pointerId);
    }

    this.stop();
  }

  onPointerMove(event) {
    if (!this.isPointerDragging) return;

    const deltaX = event.clientX - this.pointerDragStartX;
    this.pointerDragDeltaX = deltaX;
    if (Math.abs(deltaX) > 4) {
      this.pointerDragMoved = true;
    }

    if (!this.pointerDragMoved) return;

    event.preventDefault();
    this.slider.scrollLeft = this.pointerDragStartScrollLeft - deltaX * 1.35;
  }

  finishPointerDrag(event) {
    if (!this.isPointerDragging) return;

    this.isPointerDragging = false;
    this.slider.classList.remove('is-dragging');

    if (this.slider.releasePointerCapture && this.slider.hasPointerCapture(event.pointerId)) {
      this.slider.releasePointerCapture(event.pointerId);
    }

    if (this.pointerDragMoved) {
      const dragThreshold = Math.min(70, Math.max(28, this.slider.clientWidth * 0.04));
      const direction = Math.abs(this.pointerDragDeltaX) >= dragThreshold ? (this.pointerDragDeltaX < 0 ? 1 : -1) : 0;
      this.goToSlide(direction ? this.pointerDragStartIndex + direction : this.currentSlideIndex());
    }

    this.restart();
  }

  onPointerUp(event) {
    this.finishPointerDrag(event);
  }

  onPointerCancel(event) {
    this.finishPointerDrag(event);
    this.pointerDragMoved = false;
  }

  onPointerClick(event) {
    if (!this.pointerDragMoved) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.pointerDragMoved = false;
  }

  next() {
    const count = this.pageCount();
    if (count <= 1) return;

    // Ping-pong so it loops without a jarring rewind.
    let target = this.currentPage() + this.direction;
    if (target >= count) {
      this.direction = -1;
      target = count - 2;
    } else if (target < 0) {
      this.direction = 1;
      target = 1;
    }
    this.goTo(target);
  }

  start() {
    if (!this.autoplay || this.reducedMotion || this.pageCount() <= 1) return;
    this.stop();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }

  throttle(fn, wait) {
    let scheduled = false;
    let lastArgs;
    return (...args) => {
      lastArgs = args;
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        fn.apply(this, lastArgs);
      }, wait);
    };
  }
}

function initFeaturedCarousels(scope) {
  (scope || document).querySelectorAll('[data-carousel-autoplay]').forEach((el) => {
    if (el.featuredCarousel) return;
    el.featuredCarousel = new FeaturedCarousel(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initFeaturedCarousels());
} else {
  initFeaturedCarousels();
}

// Re-init when a section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => initFeaturedCarousels(event.target));

// <product-recommendations> loads its content via AJAX after the page renders,
// so watch each one and (re-)init any carousel it injects.
function observeRecommendationCarousels() {
  document.querySelectorAll('product-recommendations').forEach((el) => {
    if (el.carouselObserver) return;
    el.carouselObserver = new MutationObserver(() => initFeaturedCarousels(el));
    el.carouselObserver.observe(el, { childList: true });
  });
}

observeRecommendationCarousels();
document.addEventListener('shopify:section:load', observeRecommendationCarousels);
